import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "../lib/api";
import { Reveal } from "../components/common/Reveal";
import { SectionLabel } from "../components/common/SectionLabel";
import { TechCard } from "../components/common/TechCard";
import SEO from "../components/common/SEO";

const TYPE_FILTERS = [
    { key: "all", label: "All" },
    { key: "blog", label: "Blog" },
    { key: "news", label: "News" },
    { key: "event", label: "Events" },
];

const PAGE_SIZE = 9;

function formatDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "2-digit",
        });
    } catch {
        return iso;
    }
}

export default function Insights() {
    const [type, setType] = useState("all");
    const [category, setCategory] = useState("all");
    const [searchInput, setSearchInput] = useState("");
    const [searchTerm, setSearchTerm] = useState(""); // debounced
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [pages, setPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [knownCategories, setKnownCategories] = useState([]);
    const debounceRef = useRef();

    // Debounce search input → searchTerm (300ms)
    useEffect(() => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setSearchTerm(searchInput.trim());
        }, 300);
        return () => clearTimeout(debounceRef.current);
    }, [searchInput]);

    // Reset to page 1 whenever a filter or search changes
    useEffect(() => {
        setPage(1);
    }, [type, category, searchTerm]);

    // Load data
    useEffect(() => {
        let cancel = false;
        const load = async () => {
            if (page === 1) setLoading(true);
            else setLoadingMore(true);
            const params = { page, limit: PAGE_SIZE };
            if (type !== "all") params.type = type;
            if (category !== "all") params.category = category;
            if (searchTerm) params.q = searchTerm;
            try {
                const { data } = await api.get("/posts-search", { params });
                if (cancel) return;
                const newItems = data.items || [];
                setItems((prev) => (page === 1 ? newItems : [...prev, ...newItems]));
                setTotal(data.total || 0);
                setPages(data.pages || 0);
                // Collect categories for chips (only on page 1 no-filter requests so chips stay stable)
                if (page === 1 && category === "all" && !searchTerm) {
                    const cats = new Set();
                    newItems.forEach((p) => p.category && cats.add(p.category));
                    setKnownCategories(Array.from(cats));
                }
            } finally {
                if (!cancel) {
                    setLoading(false);
                    setLoadingMore(false);
                }
            }
        };
        load();
        return () => {
            cancel = true;
        };
    }, [type, category, searchTerm, page]);

    const categoryChips = useMemo(() => ["all", ...knownCategories], [knownCategories]);

    const hasMore = page < pages;

    return (
        <>
            <SEO
                title="Insights — Signal, Not Noise · Skovomax"
                description="Essays, updates and events from the Skovomax engineering and research team — photonics, GeoAI, and systems thinking."
            />
            <section className="pt-28 pb-10 border-b border-white/10 relative overflow-hidden">
                <div className="absolute inset-0 photonic-grid opacity-20 pointer-events-none" />
                <div className="max-w-[1440px] mx-auto px-6 md:px-10">
                    <Reveal>
                        <SectionLabel label="Insights / 07" />
                        <h1 className="font-serif-display text-6xl md:text-8xl leading-[0.95] tracking-tight max-w-5xl">
                            Signal, <br />
                            <span className="italic text-white/70">not noise.</span>
                        </h1>
                        <p className="mt-8 max-w-2xl text-white/60 text-lg leading-relaxed">
                            Essays, updates and events from the Skovomax engineering and research team —
                            photonics, GeoAI, and systems thinking.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* Filters + Search */}
            <section className="py-10 border-b border-white/10 bg-[#08081a]">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex flex-col gap-6">
                    <div className="flex flex-col md:flex-row gap-6 md:items-center md:justify-between">
                        <div className="flex flex-wrap items-center gap-3">
                            <span className="tech-label">Type:</span>
                            {TYPE_FILTERS.map((f) => (
                                <button
                                    key={f.key}
                                    onClick={() => setType(f.key)}
                                    className={`chip ${type === f.key ? "chip-active" : ""}`}
                                    data-testid={`filter-type-${f.key}`}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>
                        <div className="relative w-full md:w-[340px]">
                            <Search
                                size={14}
                                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none"
                            />
                            <input
                                type="search"
                                placeholder="Search title, tag, excerpt…"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                className="tech-input !pl-10 !py-2.5 !text-[13px] w-full"
                                data-testid="insights-search-input"
                                aria-label="Search Insights"
                            />
                        </div>
                    </div>
                    {categoryChips.length > 1 && (
                        <div className="flex flex-wrap items-center gap-3">
                            <span className="tech-label">Category:</span>
                            {categoryChips.map((c) => (
                                <button
                                    key={c}
                                    onClick={() => setCategory(c)}
                                    className={`chip ${category === c ? "chip-active" : ""}`}
                                    data-testid={`filter-category-${c}`}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            <section className="py-20">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10">
                    {loading && page === 1 && (
                        <div className="tech-label" data-testid="insights-loading">Loading signals…</div>
                    )}
                    {!loading && items.length === 0 && (
                        <div className="tech-label" data-testid="insights-empty">
                            {searchTerm
                                ? `No entries match “${searchTerm}”.`
                                : "No entries match current filters."}
                        </div>
                    )}
                    {total > 0 && (
                        <div
                            className="mb-8 font-mono-tech text-[11px] tracking-[0.22em] uppercase text-white/45"
                            data-testid="insights-count"
                        >
                            Showing {items.length} / {total}
                            {searchTerm && <> · for “{searchTerm}”</>}
                        </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {items.map((p, i) => (
                            <Reveal delay={(i % PAGE_SIZE) * 60} key={p.id}>
                                <Link to={`/insights/${p.slug}`} data-testid={`insight-card-${p.slug}`}>
                                    <TechCard className="!p-0 h-full overflow-hidden">
                                        {p.cover_image && (
                                            <div className="aspect-[4/3] overflow-hidden border-b border-white/10">
                                                <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" />
                                            </div>
                                        )}
                                        <div className="p-7">
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className="chip chip-active">{p.type}</div>
                                                {p.category && <div className="chip">{p.category}</div>}
                                            </div>
                                            <h3 className="font-serif-display text-2xl leading-tight mb-3">{p.title}</h3>
                                            <p className="text-white/55 text-sm leading-relaxed line-clamp-3">
                                                {p.excerpt}
                                            </p>
                                            <div className="mt-6 font-mono-tech text-[11px] tracking-[0.2em] uppercase text-white/45">
                                                {p.type === "event" && p.event_date
                                                    ? `EVENT · ${formatDate(p.event_date)}`
                                                    : formatDate(p.published_at || p.created_at)}
                                            </div>
                                        </div>
                                    </TechCard>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                    {hasMore && (
                        <div className="mt-14 flex justify-center">
                            <button
                                onClick={() => setPage((p) => p + 1)}
                                className="btn-secondary"
                                disabled={loadingMore}
                                data-testid="insights-load-more"
                            >
                                {loadingMore ? "Loading…" : "Load more"}
                            </button>
                        </div>
                    )}
                </div>
            </section>
        </>
    );
}
